/**
 * Permission Groups for UI Display
 *
 * Groups permissions by functional area for easier management in the
 * Custom Roles UI. Each group contains related permissions that are
 * typically granted together.
 *
 * @see Issue #293 - Permission System Enhancement
 */

import { Permission, type PermissionValue } from '../enums';

/**
 * Permission group definition
 */
export interface PermissionGroupDefinition {
  /** Display name for the group */
  name: string;
  /** Description of what this group controls */
  description: string;
  /** Icon identifier for UI */
  icon?: string;
  /** Permissions in this group */
  permissions: PermissionValue[];
}

/**
 * Permission groups organized by functional area.
 *
 * Used by CustomRoles management UI to present permissions in
 * logical categories rather than a flat list.
 */
export const PERMISSION_GROUPS: Record<string, PermissionGroupDefinition> = {
  /**
   * Loan Operations - Core lending workflow permissions
   * License-gated: Most require NMLS or state licenses
   */
  LOAN_OPERATIONS: {
    name: 'Loan Operations',
    description: 'Loan origination, processing, and servicing',
    icon: 'AttachMoney',
    permissions: [
      Permission.loan.originate,
      Permission.loan.process,
      Permission.loan.underwrite,
      Permission.loan.fund,
      Permission.loan.service,
      Permission.loan.modify,
      Permission.loan.approveModification,
    ],
  },

  /**
   * Consumer Data Access - PII and financial data
   * License-gated: Requires NMLS for compliance
   */
  CONSUMER_DATA: {
    name: 'Consumer Data Access',
    description: 'Access to consumer PII and financial information',
    icon: 'Security',
    permissions: [Permission.consumer.piiAccess, Permission.consumer.creditPull, Permission.consumer.financialsView],
  },

  /**
   * Document Management - Standard and sensitive documents
   */
  DOCUMENTS: {
    name: 'Document Management',
    description: 'Upload, view, and manage documents',
    icon: 'Description',
    permissions: [
      Permission.documents.read,
      Permission.documents.upload,
      Permission.documents.delete,
      Permission.documents.analyze,
      Permission.documents.verify,
      Permission.documents.sensitiveRead,
      Permission.documents.restrictedRead,
    ],
  },

  /**
   * Pipeline & Workflow - Case and stage management
   */
  PIPELINE: {
    name: 'Pipeline & Workflow',
    description: 'Stage transitions and approval workflows',
    icon: 'Timeline',
    permissions: [
      Permission.pipeline.stageTransition,
      Permission.pipeline.dualControlApprove,
      Permission.pipeline.amountOverride,
    ],
  },

  /**
   * AI Agents - Agent configuration and usage
   */
  AGENTS: {
    name: 'AI Agents',
    description: 'Create and manage AI agents',
    icon: 'AutoAwesome',
    permissions: [
      Permission.agents.read,
      Permission.agents.create,
      Permission.agents.update,
      Permission.agents.delete,
      Permission.agents.deploy,
    ],
  },

  /**
   * Conversations - Chat and messaging
   */
  CONVERSATIONS: {
    name: 'Conversations',
    description: 'View and manage conversations',
    icon: 'Chat',
    permissions: [
      Permission.conversations.read,
      Permission.conversations.create,
      Permission.conversations.delete,
      Permission.messages.read,
      Permission.messages.create,
    ],
  },

  /**
   * Document Intelligence - Rulesets and findings
   */
  DOCUMENT_INTELLIGENCE: {
    name: 'Document Intelligence',
    description: 'Document analysis rules and findings',
    icon: 'FindInPage',
    permissions: [
      Permission.rulesets.read,
      Permission.rulesets.create,
      Permission.rulesets.update,
      Permission.rulesets.delete,
      Permission.findings.read,
      Permission.findings.update,
    ],
  },

  /**
   * Knowledge Base - RAG and knowledge management
   */
  KNOWLEDGE_BASE: {
    name: 'Knowledge Base',
    description: 'Manage knowledge sources for AI',
    icon: 'MenuBook',
    permissions: [
      Permission.knowledgeBase.read,
      Permission.knowledgeBase.create,
      Permission.knowledgeBase.update,
      Permission.knowledgeBase.delete,
    ],
  },

  /**
   * User Management - User administration within business
   */
  USER_MANAGEMENT: {
    name: 'User Management',
    description: 'Manage users and team members',
    icon: 'Group',
    permissions: [Permission.users.read, Permission.users.create, Permission.users.update, Permission.users.delete],
  },

  /**
   * Team Management - Team creation and membership
   */
  TEAM_MANAGEMENT: {
    name: 'Team Management',
    description: 'Create and manage teams',
    icon: 'Groups',
    permissions: [
      Permission.teams.create,
      Permission.teams.update,
      Permission.teams.delete,
      Permission.teams.manageMembers,
    ],
  },

  /**
   * API & Integrations - Developer access
   */
  API_INTEGRATIONS: {
    name: 'API & Integrations',
    description: 'API keys and external integrations',
    icon: 'Api',
    permissions: [
      Permission.apiKeys.read,
      Permission.apiKeys.create,
      Permission.apiKeys.revoke,
      Permission.integrations.read,
      Permission.integrations.create,
      Permission.integrations.update,
      Permission.integrations.delete,
    ],
  },

  /**
   * Business Settings - Configuration
   */
  SETTINGS: {
    name: 'Business Settings',
    description: 'Business configuration and preferences',
    icon: 'Settings',
    permissions: [Permission.settings.read, Permission.settings.update],
  },

  /**
   * Analytics & Reporting - Data insights
   */
  ANALYTICS: {
    name: 'Analytics & Reporting',
    description: 'View and export analytics data',
    icon: 'BarChart',
    permissions: [
      Permission.analytics.read,
      Permission.analytics.export,
      Permission.audit.read,
      Permission.audit.export,
    ],
  },

  /**
   * Billing - Subscription and payment management
   */
  BILLING: {
    name: 'Billing',
    description: 'View and manage billing',
    icon: 'CreditCard',
    permissions: [Permission.billing.read, Permission.billing.update],
  },

  /**
   * Notifications - Alert configuration
   */
  NOTIFICATIONS: {
    name: 'Notifications',
    description: 'Manage notification rules and templates',
    icon: 'Notifications',
    permissions: [
      Permission.notifications.read,
      Permission.notifications.readAll,
      Permission.notifications.create,
      Permission.notifications.update,
      Permission.notifications.delete,
      Permission.notifications.rules.read,
      Permission.notifications.rules.create,
      Permission.notifications.rules.update,
      Permission.notifications.rules.delete,
      Permission.notifications.templates.read,
      Permission.notifications.templates.create,
      Permission.notifications.templates.update,
      Permission.notifications.templates.delete,
    ],
  },

  /**
   * Domain Management - Custom domain configuration and approval
   */
  DOMAIN_MANAGEMENT: {
    name: 'Domain Management',
    description: 'Custom domain configuration and approval',
    icon: 'Language',
    permissions: [Permission.customDomain.manage, Permission.customDomain.approve],
  },

  /**
   * Entity Framework — no-code custom entities, fields, and relationships (Issue #1653)
   */
  ENTITY_FRAMEWORK: {
    name: 'Entity Framework',
    description: 'Create and manage custom entities, fields, and relationships',
    icon: 'Schema',
    permissions: [
      Permission.entities.read,
      Permission.entities.create,
      Permission.entities.update,
      Permission.entities.delete,
      Permission.entities.fields.create,
      Permission.entities.fields.update,
      Permission.entities.fields.delete,
      Permission.entities.fields.reorder,
      Permission.entities.relationships.create,
      Permission.entities.relationships.delete,
    ],
  },

  /**
   * Partner Management (Issue #484)
   */
  PARTNER_MANAGEMENT: {
    name: 'Partner Management',
    description: 'Manage partner invitations and relationships',
    icon: 'Handshake',
    permissions: [
      Permission.partners.read,
      Permission.partners.invite,
      Permission.partners.refer,
      Permission.partners.manage,
      Permission.partners.delete,
      Permission.partners.agreements.read,
      Permission.partners.agreements.create,
      Permission.partners.agreements.update,
      Permission.partners.agreements.approve,
    ],
  },
};

/**
 * Get all permission group names
 */
export function getPermissionGroupNames(): string[] {
  return Object.keys(PERMISSION_GROUPS);
}

/**
 * Get a permission group by name
 */
export function getPermissionGroup(name: string): PermissionGroupDefinition | undefined {
  return PERMISSION_GROUPS[name];
}

/**
 * Get all permissions across all groups (deduplicated)
 */
export function getAllGroupedPermissions(): PermissionValue[] {
  const allPermissions = new Set<PermissionValue>();
  for (const group of Object.values(PERMISSION_GROUPS)) {
    for (const permission of group.permissions) {
      allPermissions.add(permission);
    }
  }
  return Array.from(allPermissions);
}

/**
 * Find which group(s) a permission belongs to
 */
export function getGroupsForPermission(permission: PermissionValue): string[] {
  const groups: string[] = [];
  for (const [groupName, group] of Object.entries(PERMISSION_GROUPS)) {
    if (group.permissions.includes(permission)) {
      groups.push(groupName);
    }
  }
  return groups;
}

/**
 * Permission group order for UI display
 * Groups are shown in this order in the Custom Roles UI
 */
export const PERMISSION_GROUP_ORDER: readonly string[] = [
  'LOAN_OPERATIONS',
  'CONSUMER_DATA',
  'PIPELINE',
  'DOCUMENTS',
  'AGENTS',
  'CONVERSATIONS',
  'DOCUMENT_INTELLIGENCE',
  'KNOWLEDGE_BASE',
  'USER_MANAGEMENT',
  'TEAM_MANAGEMENT',
  'ENTITY_FRAMEWORK',
  'PARTNER_MANAGEMENT',
  'DOMAIN_MANAGEMENT',
  'API_INTEGRATIONS',
  'SETTINGS',
  'ANALYTICS',
  'BILLING',
  'NOTIFICATIONS',
] as const;

/**
 * Get permission groups in display order
 */
export function getPermissionGroupsOrdered(): Array<{ key: string; group: PermissionGroupDefinition }> {
  return PERMISSION_GROUP_ORDER.map((key) => ({
    key,
    group: PERMISSION_GROUPS[key],
  })).filter((item): item is { key: string; group: PermissionGroupDefinition } => item.group !== undefined);
}
